import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Globe, Link2, Lock, Rocket } from 'lucide-react';
import { Button, Input, Modal } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { cn } from '@/lib/utils';
import { queryKeys } from '@/lib/queryClient';
import { useAuth } from '@/app/providers';
import type { Channel } from '@/features/communities/community-view/channels';
import { updateCommunity } from '@/features/communities/api/communities';
import { replaceCommunityChannels } from '@/features/communities/api/channels';
import { channelKeys } from '@/features/communities/hooks/useChannels';
import { applyThemeToCommunity, publishTheme, renameTheme, setThemeVisibility } from '../api/themes';
import { uploadThemeAsset } from '../api/assets';
import { exportBannerImage } from '../model/bannerLayers';
import { useEditorStore } from '../state/editorStore';
import { themeKeys } from '../hooks/useThemeDoc';

type Visibility = 'private' | 'unlisted' | 'public';

const VISIBILITIES: { id: Visibility; label: string; desc: string; icon: typeof Lock }[] = [
  { id: 'private', label: 'Private', desc: 'Only you', icon: Lock },
  { id: 'unlisted', label: 'Unlisted', desc: 'Anyone with the link', icon: Link2 },
  { id: 'public', label: 'Public', desc: 'Listed in the marketplace', icon: Globe },
];

/**
 * Publishing is a moment, not a settings write: name it, choose who can see it,
 * ship it. For community owners this also applies the theme live (snapshot to
 * the `communities` row), persists identity fields, and saves the channel rail
 * — everything a member sees, in one confirm.
 */
export function PublishDialog({
  open,
  onClose,
  communityId,
  communityName,
  priorChannels,
  onFlush,
}: {
  open: boolean;
  onClose: () => void;
  /** Set (and owned) when publishing should also apply to a community. */
  communityId?: string;
  communityName?: string;
  /** Live channel rows — preserves topics/read-only flags the studio doesn't edit. */
  priorChannels: Channel[];
  onFlush: () => Promise<void>;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const storeName = useEditorStore((s) => s.themeName);
  const [name, setName] = useState<string | null>(null);
  const [visibility, setVisibility] = useState<Visibility>(communityId ? 'private' : 'public');
  const [busy, setBusy] = useState(false);
  const value = name ?? storeName;

  async function publish(): Promise<void> {
    const s = useEditorStore.getState();
    setBusy(true);
    try {
      await onFlush();
      const themeId = useEditorStore.getState().themeRowId;
      if (!themeId) throw new Error('Nothing to publish yet — make an edit first.');

      const trimmed = value.trim() || 'Untitled theme';
      if (trimmed !== storeName) {
        await renameTheme(themeId, trimmed);
        s.setThemeRow(themeId, trimmed);
      }
      await publishTheme(themeId);
      await setThemeVisibility(themeId, visibility);

      if (communityId) {
        await applyThemeToCommunity(themeId, communityId);
        const flat = useEditorStore.getState().flat;
        const tagline = flat.tagline.trim() || null;

        // Layered banner designs flatten to a hosted image so non-themed
        // surfaces (explore/directory cards) show them too; themed surfaces
        // render the live layers from the doc snapshot.
        let bannerUrl = flat.banner && !flat.banner.startsWith('data:') ? flat.banner : null;
        if (user && flat.bannerDesign?.layers.length) {
          const blob = await exportBannerImage(flat.bannerDesign);
          bannerUrl = (await uploadThemeAsset(blob, 'banner', user.id)).url;
        }

        await updateCommunity(communityId, {
          name: flat.communityName.trim() || communityName || undefined,
          tagline,
          description: tagline,
          color: flat.accent,
          banner: bannerUrl,
        });
        const priorById = new Map(priorChannels.map((c) => [c.id, c]));
        await replaceCommunityChannels(
          communityId,
          flat.channels.map((ch) => {
            const prev = priorById.get(ch.id);
            return {
              id: ch.id,
              name: ch.name,
              kind: ch.kind,
              section: ch.section || prev?.section || (ch.kind === 'voice' ? 'VOICE CHANNELS' : 'TEXT CHANNELS'),
              ...(ch.icon ? { icon: ch.icon } : {}),
              ...(ch.effect && ch.effect !== 'none' ? { effect: ch.effect } : {}),
              ...(prev?.topic ? { topic: prev.topic } : {}),
              ...(prev?.readOnly ? { readOnly: true } : {}),
            };
          }),
        );
        void qc.invalidateQueries({ queryKey: queryKeys.communities.all });
        void qc.invalidateQueries({ queryKey: channelKeys.list(communityId) });
      }

      void qc.invalidateQueries({ queryKey: themeKeys.all });
      toast(
        communityId
          ? 'Published — your members see it now'
          : visibility === 'public'
            ? 'Published to the marketplace'
            : 'Published',
        'ok',
      );
      onClose();
    } catch (e) {
      toast((e as Error).message || 'Could not publish', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <div className="mb-1 flex items-center gap-2 text-lg font-extrabold text-hi">
        <Rocket size={18} className="text-accent" />
        {communityId ? `Publish to ${communityName ?? 'your community'}` : 'Publish theme'}
      </div>
      <p className="mb-5 text-[12.5px] leading-relaxed text-mid">
        {communityId
          ? 'Every member sees this theme the moment you publish.'
          : 'Publishing makes this theme installable — on your communities, and by others if you list it.'}
      </p>

      <label className="mb-1.5 block text-[10.5px] font-bold uppercase tracking-[0.6px] text-lo">
        Theme name
      </label>
      <Input value={value} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Untitled theme" />

      <div className="mb-1.5 mt-4 text-[10.5px] font-bold uppercase tracking-[0.6px] text-lo">Sharing</div>
      <div className="space-y-1.5">
        {VISIBILITIES.map((v) => (
          <button
            key={v.id}
            type="button"
            onClick={() => setVisibility(v.id)}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
              visibility === v.id ? 'border-accent/50 bg-accent/[0.08]' : 'border-white/10 hover:bg-white/[0.04]',
            )}
          >
            <v.icon size={15} className={visibility === v.id ? 'text-accent' : 'text-mid'} />
            <span className="min-w-0">
              <span className="block text-[13px] font-bold text-hi">{v.label}</span>
              <span className="block text-[11px] text-lo">{v.desc}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void publish()} disabled={busy} className="gap-1.5">
          {busy ? 'Publishing…' : communityId ? 'Publish & apply' : 'Publish'}
        </Button>
      </div>
    </Modal>
  );
}
